import classNames from 'classnames';
import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { RewardUnlockedAnimation } from '@sorare/core/src/components/rewards/RewardUnlockedAnimation';
import { useSeriesNav } from '@sorare/core/src/hooks/commonGame/useSeriesNav.ts';
import { glossary } from '@sorare/core/src/lib/glossary';
import { useCommonGameSet } from '@sorare/core/src/set/index.ts';

import { ButtonsWrapper, Root } from './styles.tsx';

type Props = {
  header?: ReactNode;
  onClose: () => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  amount?: ReactNode;
  imgUrls: string[];
  rgbColor?: string;
  children?: ReactNode;
  action?: ReactNode;
  showContinueButton?: boolean;
  continueButtonColor?: 'primary' | 'secondary';
  hideBackground?: boolean;
  useSetBackground?: boolean;
  disclaimer?: ReactNode;
};
export const DumbRewardUnlockedStory = ({
  header,
  onClose,
  title = <FormattedMessage defaultMessage="You've won a reward!" />,
  subtitle,
  amount,
  action,
  imgUrls,
  rgbColor = 'var(--c-rgb-green-400)',
  children,
  showContinueButton = true,
  continueButtonColor = 'primary',
  hideBackground,
  useSetBackground,
  disclaimer,
}: Props) => {
  const isSeriesNav = useSeriesNav();
  const { visual } = useCommonGameSet();
  const shouldUseSetBackground =
    useSetBackground || (isSeriesNav && !!visual.welcomeBackDialog);
  const shouldHideBackground = hideBackground || shouldUseSetBackground;
  return (
    <Root
      style={
        {
          '--rgb-color': rgbColor,
          ...(shouldUseSetBackground ? visual.welcomeBackDialog : {}),
        } as unknown as CSSProperties
      }
      className={classNames({ hideBackground: shouldHideBackground })}
    >
      {header}
      <RewardUnlockedAnimation
        imgUrls={imgUrls}
        title={title}
        subtitle={subtitle}
        amount={amount}
        shouldUseSetBackground={shouldUseSetBackground}
      >
        {children}
      </RewardUnlockedAnimation>
      <ButtonsWrapper>
        {disclaimer}
        {action}
        {!!showContinueButton && (
          <Button
            size="large"
            onClick={() => {
              onClose?.();
            }}
            color={continueButtonColor}
            disableDebounce
          >
            <FormattedMessage {...glossary.continue} />
          </Button>
        )}
      </ButtonsWrapper>
    </Root>
  );
};
